import { z } from 'zod/v4';
import { FONT_SCHEMA, IFont } from '../font.interfaces';
import {
  ACCESSIBLE_IMAGE_SCHEMA,
  DEFAULT_ACCESSIBLE_IMAGE_BANNER,
  IAccessibleImage,
} from '../image.interfaces';
import { EEntityType, ERelationType } from '../text-relation-type.enums';
import { ETextType } from '../text/text.enums';
import {
  INormalText,
  NORMAL_TEXT_SCHEMA,
  NormalTextId,
  TextLanguageValueId,
} from '../text/text.interfaces';
import {
  EBannerHorizontalAlignment,
  EBannerVerticalAlignment,
} from './banner.enums';
import { Brand } from '../../branded-types';
import { LanguagePrefix } from '../language.interfaces';

export type BannerId = Brand<string, 'bannerId'>;

export const BANNER_SCHEMA: z.ZodType<IBanner> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as BannerId)
    .optional(),
  isImageActive: z.boolean(),
  accessibleImage: ACCESSIBLE_IMAGE_SCHEMA,
  imageHeight: z.number(),
  isTextActive: z.boolean(),
  texts: z.array(NORMAL_TEXT_SCHEMA),
  headlineFontSize: z.number(),
  subHeadlineFontSize: z.number(),
  textFont: FONT_SCHEMA.nullable().default(null),
  textColor: z.string(),
  textAlignmentVertical: z.enum(EBannerVerticalAlignment),
  textAlignmentHorizontal: z.enum(EBannerHorizontalAlignment),
});

export interface IBanner {
  id?: BannerId;
  isImageActive: boolean;
  accessibleImage: IAccessibleImage;
  imageHeight: number;
  isTextActive: boolean;
  texts: INormalText[];
  headlineFontSize: number;
  subHeadlineFontSize: number;
  textFont: IFont | null;
  textColor: string;
  textAlignmentVertical: EBannerVerticalAlignment;
  textAlignmentHorizontal: EBannerHorizontalAlignment;
}

export const DEFAULT_BANNER: IBanner = {
  id: '0d9b64e0-ffe7-428b-ab12-491ae831d0e4' as BannerId,
  isImageActive: true,
  accessibleImage: DEFAULT_ACCESSIBLE_IMAGE_BANNER,
  imageHeight: 400,
  isTextActive: true,
  texts: [
    {
      id: '88595c7f-7a09-4722-b0cb-f39e77f4c850' as NormalTextId,
      text: {
        values: [
          {
            id: '6a157aa6-1115-4050-8f12-bd9b20079130' as TextLanguageValueId,
            value: 'Dein Ticket, dein Weg',
            languagePrefix: 'DE' as LanguagePrefix,
          },
        ],
      },
      textType: ETextType.Web,
      relationType: ERelationType.headline,
      entityType: EEntityType.banner,
    },
    {
      id: '784c60e9-a2b3-45af-9a78-a33d94d9de8a' as NormalTextId,
      text: {
        values: [
          {
            id: '2efb3bb9-006a-48c2-8578-4a728b5ffcde' as TextLanguageValueId,
            value: 'Einfach online buchen und stressfrei reisen!',
            languagePrefix: 'DE' as LanguagePrefix,
          },
        ],
      },
      textType: ETextType.Web,
      relationType: ERelationType.subHeadline,
      entityType: EEntityType.banner,
    },
  ],
  headlineFontSize: 32,
  subHeadlineFontSize: 14,
  textFont: null,
  textColor: '#FFFFFF',
  textAlignmentVertical: EBannerVerticalAlignment.Mitte,
  textAlignmentHorizontal: EBannerHorizontalAlignment.Mitte,
};
